import { useEffect, useRef, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import PropTypes from 'prop-types';

import {
    startSessionRequest,
    endSessionRequest,
    sendHeartbeatRequest,
    sendPageViewsBatchRequest,
    sendEventsBatchRequest,
    queuePageView,
    setCurrentPage,
} from '../core/analytics/analyticsActions';
import { getPageCategory } from '../core/analytics/pageCategories';

const HEARTBEAT_INTERVAL = 60000; // 60 seconds
const BATCH_INTERVAL = 30000; // 30 seconds

function AnalyticsProvider({ children }) {
    const dispatch = useDispatch();
    const location = useLocation();

    const { rehydrationCompleted } = useSelector(state => state.global);
    const analytics = useSelector(state => state.analytics);

    const heartbeatTimerRef = useRef(null);
    const batchTimerRef = useRef(null);
    const isSessionStartedRef = useRef(false);

    // Start heartbeat
    const startHeartbeat = useCallback(() => {
        if (heartbeatTimerRef.current) {
            clearInterval(heartbeatTimerRef.current);
        }

        heartbeatTimerRef.current = setInterval(() => {
            dispatch(sendHeartbeatRequest());
        }, HEARTBEAT_INTERVAL);
    }, [dispatch]);

    // Stop heartbeat
    const stopHeartbeat = useCallback(() => {
        if (heartbeatTimerRef.current) {
            clearInterval(heartbeatTimerRef.current);
            heartbeatTimerRef.current = null;
        }
    }, []);

    // Start batch processing
    const startBatchProcessing = useCallback(() => {
        if (batchTimerRef.current) {
            clearInterval(batchTimerRef.current);
        }

        batchTimerRef.current = setInterval(() => {
            dispatch(sendPageViewsBatchRequest());
            dispatch(sendEventsBatchRequest());
        }, BATCH_INTERVAL);
    }, [dispatch]);

    // Stop batch processing
    const stopBatchProcessing = useCallback(() => {
        if (batchTimerRef.current) {
            clearInterval(batchTimerRef.current);
            batchTimerRef.current = null;
        }
    }, []);

    // Flush queues
    const flushQueues = useCallback(() => {
        dispatch(sendPageViewsBatchRequest());
        dispatch(sendEventsBatchRequest());
    }, [dispatch]);

    // Initialize session after rehydration
    useEffect(() => {
        if (rehydrationCompleted && !isSessionStartedRef.current) {
            dispatch(startSessionRequest());
            isSessionStartedRef.current = true;
        }
    }, [rehydrationCompleted, dispatch]);

    // Start heartbeat and batch processing when session is available
    useEffect(() => {
        if (analytics.sessionId) {
            startHeartbeat();
            startBatchProcessing();
        }

        return () => {
            stopHeartbeat();
            stopBatchProcessing();
        };
    }, [analytics.sessionId, startHeartbeat, startBatchProcessing, stopHeartbeat, stopBatchProcessing]);

    // Track page views on route change
    useEffect(() => {
        if (analytics.sessionId && location.pathname) {
            // Queue previous page view with time spent
            if (analytics.currentPage && analytics.pageStartTime) {
                const timeSpent = Math.round((Date.now() - analytics.pageStartTime) / 1000);
                dispatch(queuePageView({
                    page_category: analytics.currentPage.category,
                    page_path: analytics.currentPage.path,
                    page_title: analytics.currentPage.title,
                    referrer_path: null,
                    time_spent_seconds: timeSpent,
                    viewed_at: analytics.currentPage.viewedAt,
                }));
            }

            // Set current page
            const category = getPageCategory(location.pathname);
            const title = document.title || location.pathname;
            dispatch(setCurrentPage({
                category,
                path: location.pathname,
                title,
                viewedAt: new Date().toISOString(),
            }));
        }
    }, [location.pathname, analytics.sessionId, dispatch]);

    // Handle page unload
    useEffect(() => {
        const handleBeforeUnload = () => {
            // Track time on last page
            if (analytics.currentPage && analytics.pageStartTime && analytics.sessionId) {
                const timeSpent = Math.round((Date.now() - analytics.pageStartTime) / 1000);
                const pageViews = [
                    ...analytics.pageViewQueue.toJS(),
                    {
                        page_category: analytics.currentPage.category,
                        page_path: analytics.currentPage.path,
                        page_title: analytics.currentPage.title,
                        referrer_path: null,
                        time_spent_seconds: timeSpent,
                        viewed_at: analytics.currentPage.viewedAt,
                    },
                ];

                // Use sendBeacon for reliable delivery
                if (pageViews.length > 0) {
                    const data = JSON.stringify({
                        session_id: analytics.sessionId,
                        page_views: pageViews,
                    });
                    window.navigator.sendBeacon('/api/analytics/page-view/batch', data);
                }

                const events = analytics.eventQueue.toJS();
                if (events.length > 0) {
                    const eventData = JSON.stringify({
                        session_id: analytics.sessionId,
                        events,
                    });
                    window.navigator.sendBeacon('/api/analytics/event/batch', eventData);
                }
            }
        };

        const handleVisibilityChange = () => {
            if (document.hidden) {
                flushQueues();
            }
        };

        window.addEventListener('beforeunload', handleBeforeUnload);
        document.addEventListener('visibilitychange', handleVisibilityChange);

        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, [analytics.currentPage, analytics.pageStartTime, analytics.pageViewQueue, analytics.eventQueue, analytics.sessionId, flushQueues]);

    // Cleanup on unmount
    useEffect(() => () => {
        stopHeartbeat();
        stopBatchProcessing();
        if (analytics.sessionId) {
            dispatch(endSessionRequest());
        }
    }, []);

    return children;
}

AnalyticsProvider.propTypes = {
    children: PropTypes.node.isRequired,
};

export default AnalyticsProvider;
